/**
 * search-drawer.js
 *
 * Controller for the mobile search drawer.
 * - Open / close lifecycle with is-open class and visibility toggle
 * - Scroll-lock via the same global counter used by drawer-menu.js
 * - Focus management: close button on open, trigger on close
 * - ESC key + overlay click + close button → close
 * - Reacts to shopify:section:load for theme editor safety
 */
(() => {
  const SECTION_SELECTOR   = '.header[data-section-type="header"]';
  const LOCAL_LOCK_ATTR    = 'data-theme-search-drawer-lock-count';
  const LOCAL_LOCK_CLASS   = 'theme-search-drawer-lock';
  const GLOBAL_LOCK_ATTR   = 'data-theme-scroll-lock-count';
  const GLOBAL_LOCK_CLASS  = 'theme-scroll-lock-active';
  const DRAWER_STATE_EVENT = 'theme:search-drawer:state';
  const SCROLLBAR_VAR      = '--theme-scrollbar-compensation';
  const DESKTOP_MEDIA      = '(min-width: 1000px)';
  const DEFAULT_DURATION   = 360;
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const controllers = new Map();

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const parseDurationMs = (raw) => {
    if (!raw) return NaN;
    const v = raw.trim();
    if (v.endsWith('ms')) return parseFloat(v);
    if (v.endsWith('s'))  return parseFloat(v) * 1000;
    return parseFloat(v);
  };

  const getMotionDuration = (el) => {
    const d = parseDurationMs(
      getComputedStyle(el || document.documentElement)
        .getPropertyValue('--motion-duration-normal')
    );
    return Number.isFinite(d) && d > 0 ? d : DEFAULT_DURATION;
  };

  const getScrollbarCompensation = () =>
    Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const acquireGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      const compensation = `${getScrollbarCompensation()}px`;
      root.style.setProperty(SCROLLBAR_VAR, compensation);
      body.style.setProperty(SCROLLBAR_VAR, compensation);
      root.classList.add(GLOBAL_LOCK_CLASS);
      body.classList.add(GLOBAL_LOCK_CLASS);
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(current + 1));
  };

  const releaseGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(GLOBAL_LOCK_CLASS);
      body.classList.remove(GLOBAL_LOCK_CLASS);
      root.removeAttribute(GLOBAL_LOCK_ATTR);
      root.style.removeProperty(SCROLLBAR_VAR);
      body.style.removeProperty(SCROLLBAR_VAR);
      return;
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
  };

  const lockScroll = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      root.classList.add(LOCAL_LOCK_CLASS);
      body.classList.add(LOCAL_LOCK_CLASS);
      acquireGlobalScrollLock();
    }

    root.setAttribute(LOCAL_LOCK_ATTR, String(current + 1));
  };

  const unlockScroll = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Math.max(0, Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10));
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(LOCAL_LOCK_CLASS);
      body.classList.remove(LOCAL_LOCK_CLASS);
      root.removeAttribute(LOCAL_LOCK_ATTR);
      releaseGlobalScrollLock();
      return;
    }

    root.setAttribute(LOCAL_LOCK_ATTR, String(next));
  };

  const getSectionKey = (root) =>
    root.getAttribute('data-section-id') || root.id || '';

  const dispatchSearchDrawerState = ({ open, sectionRoot, drawerId }) => {
    document.dispatchEvent(
      new CustomEvent(DRAWER_STATE_EVENT, {
        detail: {
          open: Boolean(open),
          sectionId: getSectionKey(sectionRoot),
          drawerId: drawerId || '',
        },
      })
    );
  };

  // ─── Per-section controller ───────────────────────────────────────────────

  const initSearchDrawer = (sectionRoot) => {
    const sectionId  = getSectionKey(sectionRoot);
    const drawer     = document.getElementById(`SearchDrawer-${sectionId}`);

    if (!drawer) return () => {};

    const panel     = drawer.querySelector('[data-search-drawer-panel]');
    const overlay   = drawer.querySelector('[data-search-drawer-overlay]');
    const closeBtn  = drawer.querySelector('[data-search-drawer-close]');
    const input     = drawer.querySelector('[data-search-drawer-input]');
    // Triggers live inside the sectionRoot (header-icons)
    const toggleTriggers = () =>
      Array.from(sectionRoot.querySelectorAll('[data-search-drawer-toggle]'));

    const desktopMQ  = window.matchMedia(DESKTOP_MEDIA);
    const disposers  = [];
    let isOpen       = false;
    let closeTimer   = 0;
    let lastTrigger  = null;

    const on = (target, event, handler, opts) => {
      if (!target) return;
      target.addEventListener(event, handler, opts);
      disposers.push(() => target.removeEventListener(event, handler, opts));
    };

    // ── Open ─────────────────────────────────────────────────────────────

    const open = (triggerEl) => {
      if (isOpen) return;
      if (desktopMQ.matches) return; // mobile only

      isOpen      = true;
      lastTrigger = triggerEl || toggleTriggers()[0] || null;

      // Sync trigger aria
      toggleTriggers().forEach((t) =>
        t.setAttribute('aria-expanded', 'true')
      );

      // Reveal element then animate on next frame
      drawer.removeAttribute('hidden');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-opening');

      window.requestAnimationFrame(() => {
        drawer.classList.remove('is-opening');
        drawer.classList.add('is-open');
        lockScroll();
        dispatchSearchDrawerState({
          open: true,
          sectionRoot,
          drawerId: drawer.id || '',
        });

        // Focus the search input once panel is visible so users can type
        // immediately. Fall back to the close button if the input is missing
        // (e.g. predictive search disabled mid-render).
        window.requestAnimationFrame(() => {
          const focusTarget = input || closeBtn;
          if (focusTarget && typeof focusTarget.focus === 'function') {
            focusTarget.focus({ preventScroll: true });
          }
        });
      });
    };

    // ── Close ────────────────────────────────────────────────────────────

    const close = (restoreFocus = true) => {
      if (!isOpen) return;

      isOpen = false;
      const duration = getMotionDuration(panel);

      drawer.classList.remove('is-open');
      drawer.classList.add('is-closing');
      drawer.setAttribute('aria-hidden', 'true');

      toggleTriggers().forEach((t) =>
        t.setAttribute('aria-expanded', 'false')
      );

      unlockScroll();
      dispatchSearchDrawerState({
        open: false,
        sectionRoot,
        drawerId: drawer.id || '',
      });

      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        drawer.classList.remove('is-closing');
        drawer.setAttribute('hidden', '');
      }, duration + 40); // small buffer past transition end

      if (restoreFocus && lastTrigger) {
        // Delay slightly so the element is reachable after scroll-lock lifts
        window.requestAnimationFrame(() => {
          if (lastTrigger && typeof lastTrigger.focus === 'function') {
            lastTrigger.focus({ preventScroll: true });
          }
        });
      }
    };

    // ── Event wiring ──────────────────────────────────────────────────────

    // Re-attach trigger listeners whenever DOM may change (section load)
    const bindTriggers = () => {
      // Remove old trigger listeners (tracked separately)
      triggerDisposers.forEach((fn) => fn());
      triggerDisposers.length = 0;

      toggleTriggers().forEach((t) => {
        const boundHandler = () => {
          lastTrigger = t;
          if (isOpen) {
            close(true);
          } else {
            open(t);
          }
        };
        t.addEventListener('click', boundHandler);
        triggerDisposers.push(() =>
          t.removeEventListener('click', boundHandler)
        );
      });
    };

    const triggerDisposers = [];
    bindTriggers();
    disposers.push(() => {
      triggerDisposers.forEach((fn) => fn());
      triggerDisposers.length = 0;
    });

    // Overlay click
    on(overlay, 'click', () => close(true));

    // Close button
    on(closeBtn, 'click', () => close(true));

    // ESC key
    const trapFocus = (event) => {
      const scope = panel || drawer;
      if (!scope) return;
      const focusables = Array.from(scope.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el === document.activeElement || el.getClientRects().length > 0
      );
      if (!focusables.length) {
        event.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (!scope.contains(active)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
        return;
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    on(document, 'keydown', (event) => {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
        return;
      }
      if (event.key === 'Tab') {
        trapFocus(event);
      }
    });

    // Click outside closes (if panel doesn't contain the click)
    on(document, 'click', (event) => {
      if (!isOpen) return;
      if (drawer.contains(event.target)) return;
      // Also check if click is on a toggle trigger
      if (toggleTriggers().some((t) => t.contains(event.target))) return;
      close(false);
    });

    // Close on desktop breakpoint if accidentally left open
    const onBreakpointChange = () => {
      if (desktopMQ.matches && isOpen) {
        close(false);
      }
    };

    if (typeof desktopMQ.addEventListener === 'function') {
      desktopMQ.addEventListener('change', onBreakpointChange);
      disposers.push(() =>
        desktopMQ.removeEventListener('change', onBreakpointChange)
      );
    }

    // ── Teardown ──────────────────────────────────────────────────────────

    return () => {
      window.clearTimeout(closeTimer);
      if (isOpen) {
        isOpen = false;
        unlockScroll();
        drawer.classList.remove('is-open', 'is-opening', 'is-closing');
        drawer.setAttribute('hidden', '');
        drawer.setAttribute('aria-hidden', 'true');
        dispatchSearchDrawerState({
          open: false,
          sectionRoot,
          drawerId: drawer.id || '',
        });
        toggleTriggers().forEach((t) =>
          t.setAttribute('aria-expanded', 'false')
        );
      }
      while (disposers.length) {
        const fn = disposers.pop();
        if (typeof fn === 'function') fn();
      }
    };
  };

  // ─── Mount / unmount per section ─────────────────────────────────────────

  const getSectionRootFromNode = (node) => {
    if (!node) return null;
    if (node.matches && node.matches(SECTION_SELECTOR)) return node;
    return node.querySelector ? node.querySelector(SECTION_SELECTOR) : null;
  };

  const mountSection = (sectionRoot) => {
    if (!sectionRoot) return;
    const key = getSectionKey(sectionRoot);
    if (!key) return;

    const existing = controllers.get(key);
    if (existing && typeof existing.destroy === 'function') {
      existing.destroy();
    }

    const destroy = initSearchDrawer(sectionRoot);
    controllers.set(key, { destroy });
  };

  const unmountSection = (sectionRoot) => {
    if (!sectionRoot) return;
    const key = getSectionKey(sectionRoot);
    if (!key) return;
    const ctrl = controllers.get(key);
    if (ctrl && typeof ctrl.destroy === 'function') ctrl.destroy();
    controllers.delete(key);
  };

  // ─── Bootstrap ───────────────────────────────────────────────────────────

  document.querySelectorAll(SECTION_SELECTOR).forEach(mountSection);

  document.addEventListener('shopify:section:load', (e) => {
    const root = getSectionRootFromNode(e.target);
    if (root) mountSection(root);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const root = getSectionRootFromNode(e.target);
    if (root) unmountSection(root);
  });
})();
